import React, { useEffect, useMemo, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import Analytics from 'analytics-opencare';
import config from 'app.config';
import { COUNTRY } from 'common/constants';
import { CHECKOUT_ERROR_TYPES } from 'connect/constants';
import { RequestAppointment } from 'connect/saga/handlers/appointment/request-appointment';
import { RequestOffer } from 'connect/saga/handlers/offer/request-offer';
import { selectDataPatient, selectDataUser } from 'connect/selectors';
import { PrivacyLink, TermsOfServiceLink } from 'pages/checkout/components/common/ExternalLinks';
import { Input, InputType } from 'pages/checkout/components/common/Input';
import { FormCheckbox, FormError } from 'pages/checkout/components/common/input-styled-components';
import { StepHeader, StepHeaderSubtext, StepHeaderText } from 'pages/checkout/components/common/layout-styled-components';
import { TermsDisclaimer } from 'pages/checkout/components/common/typographic-styled-components';
import { CheckoutStateHandler } from 'pages/checkout/connect/state-handlers';
import { CheckoutState, PreferredContactMethod, PreferredContactMethods } from 'pages/checkout/connect/types';
import { formatClinicAddress } from 'pages/checkout/utils/addressUtils';
import { useCheckoutRedesignAnalyticsData } from 'pages/checkout/utils/analyticsUtils';
import { formatPhoneNumber } from 'pages/checkout/utils/phoneUtils';
import { formatTimeSlot } from 'pages/checkout/utils/timeUtils';
import { getTimeFromStartTime } from 'pages/common/helpers/appointment-slot-helpers';
import { getInNetworkStatus } from 'pages/common/helpers/common-helpers';
import { getMatchResultsPageConfiguration } from 'pages/funnel/components/Search/constants';
import { useFunnelStorage } from 'pages/funnel/storage/Storage';
import { selectRequestedAppointment, selectSpecialtyId } from 'pages/legacy-checkout/connect/selectors';
import { getOfferedAppointmentType, getPatientAppUrl } from 'pages/legacy-checkout/helpers/confirmation-helpers';
import { selectDataClinic } from 'pages/profile/connect/selectors';

import { useRedirectionService } from 'services/RedirectionService';
import { Clinic } from 'types/Clinic';
import cjAffiliate from 'utils/cjAffiliate';
import { usePatch } from 'utils/customHooks/usePatch';
import { googlePlaceActionLink } from 'utils/google';
import { getPatientAge, getPatientSexAtBirth } from 'utils/patient';
import { getS3ImageUrl } from 'utils/s3Image';

import { FormRow } from '../Insurance/Insurance-styled-components';

import { DetailItem, Details } from './components/DetailItem/DetailItem';
import { InsuranceStatusBadge } from './components/InsuranceStatusBadge/InsuranceStatusBadge';
import { PromoCodeBox } from './components/PromoCodeBox/PromoCodeBox';
import {
  CheckboxLabeledInput,
  ConfirmStepWrapper,
  DetailsWrapper,
  PracticeDetails,
  PracticePhoto,
  PracticeTextWrapper,
  SubmitRequestButton,
  SubmitRequestWrapper,
  SubmitWrapper,
} from './Confirm-styled-components';
import { useAppointmentRequestData } from './requestHooks';

interface RequestedAppointment {
  slotId: string;
  provider: {
    doctor: {
      insurancePlans: any[];
    };
  };
}

const contactOptions = [
  { label: 'Phone Call', contactMethod: PreferredContactMethod.Phone },
  { label: 'SMS', contactMethod: PreferredContactMethod.SMS },
];

const contactOptionToApiPayload = (contactMethods: PreferredContactMethods) =>
  Object.keys(contactMethods)
    .filter((key) => contactMethods[key as PreferredContactMethod]);

const analytics = new Analytics();

const ErrorMessage = ({ requestAppointmentError }: { requestAppointmentError: string }) => {
  const clinic = useSelector(selectDataClinic);

  if (!requestAppointmentError) {
    return null;
  }

  const CLINIC_URL = `${config.SEARCH_APP_V2_URL}/results/${clinic.id}`;
  const PATIENT_URL = `${config.PATIENT_APP_URL}/#/appointments`;

  const errorMessage = {
    [CHECKOUT_ERROR_TYPES.SLOT_TAKEN]: (
      <span>
        The selected time slot is already booked. Please <a href={CLINIC_URL}>select another time</a>.
      </span>
    ),
    [CHECKOUT_ERROR_TYPES.APPOINTMENT_REQUESTED_ALREADY]: (
      <span>
        You have already submitted an appointment request with this
        clinic. <a href={PATIENT_URL}>Click here</a> to
        manage your appointment.
      </span>
    ),
    [CHECKOUT_ERROR_TYPES.NOT_PATIENT]:
      'You must log in as a patient to book an appointment. The email address entered is associated with a clinic or provider.',
    [CHECKOUT_ERROR_TYPES.PROVIDER_NO_ONLINE_BOOKING]: (
      <span>
        Provider decided to disable their requests for the time being,
        please select{' '}
        <a href="/results">another practice.</a>
      </span>
    ),
    [CHECKOUT_ERROR_TYPES.PROVIDER_NO_RESERVED_SLOTS]: (
      <span>
        There was an issue with this practice's calendar, please select{' '}
        <a href="/results">another practice.</a>
      </span>
    ),
  }[requestAppointmentError];

  return <>{errorMessage || 'Something went wrong while submitting your details.'}</>;
};

export const Confirm = () => {
  const dispatch = useDispatch();
  const analyticsData = useCheckoutRedesignAnalyticsData();

  const patchCheckout = usePatch<CheckoutState>({
    stateHandler: CheckoutStateHandler,
  });

  const [isLoadingPatientApp, setIsLoadingPatientApp] = useState(false);

  const funnelStorage = useFunnelStorage();
  const redirectionService = useRedirectionService();

  const user = useSelector(selectDataUser);
  const patient = useSelector(selectDataPatient);
  const clinic = useSelector<any, Clinic>(selectDataClinic);
  const specialtyId = useSelector(selectSpecialtyId);
  const requestedAppointment = useSelector<any, RequestedAppointment>(selectRequestedAppointment);
  const { slotId } = requestedAppointment;
  const checkoutState = useSelector<any, CheckoutState>(CheckoutStateHandler.selector);
  const {
    insurance,
    confirm: confirmState,
  } = checkoutState;
  const { plan: insurancePlan } = insurance || {};

  const requestOfferLoading = useSelector(RequestOffer.loading!.selector);

  const requestAppointmentLoading = useSelector(RequestAppointment.loading!.selector);
  const requestAppointmentError = useSelector(RequestAppointment.error!.selector);
  const newAppointment = useSelector(RequestAppointment.value!.selector);

  const requestedAppointmentType = getOfferedAppointmentType(
    requestedAppointment.provider,
    specialtyId,
  );

  const [submitting, setSubmitting] = useState(false);

  const isClinicUS = clinic.address.country === COUNTRY.unitedStates;

  const isInNetwork = useMemo(() => {
    if (!insurancePlan?.id) {
      return false;
    }

    return getInNetworkStatus(
      insurancePlan.id,
      clinic.insurancePlans,
    );
  }, [
    insurancePlan?.id,
    clinic.insurancePlans,
  ]);

  const selectedSlot = useMemo(() => {
    const slot = clinic.slots?.find((slot: any) => slot.id === slotId);
    if (!slot) {
      return null;
    }
    return slot;
  }, [clinic.slots, slotId]);

  const { date: slotDate, time: slotTime } = selectedSlot || {};

  const {
    preferredContactMethods: storedPreferredContactMethods,
    note: storedNote,
  } = confirmState || {};

  const [preferredContactMethods, setPreferredContactMethods] =
    useState<PreferredContactMethods>(storedPreferredContactMethods || []);
  const [note, setNote] = useState(storedNote || '');

  const clinicPhotoUrl = useMemo(() => getS3ImageUrl(clinic.primaryPhoto), [clinic.primaryPhoto]);
  const formattedAddress = useMemo(() => formatClinicAddress(clinic.address), [clinic.address]);
  const formattedTimeSlot = useMemo(() => {
    if (!slotDate || !slotTime) {
      return '';
    }
    return formatTimeSlot(slotDate, slotTime);
  }, [slotDate, slotTime]);

  const appointmentRequestData = useAppointmentRequestData();

  useEffect(() => {
    analytics.trackPageView({
      pageType: analytics.pageTypes.CHECKOUT,
      pageName: 'checkout-confirm',
      name: 'checkout',
      section: 'confirm',
      ...analyticsData,
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps -- analyticsData is a new object every time, so we don't want it in the dependency array
  }, []);

  const handleSubmit = () => {
    patchCheckout({
      confirm: {
        preferredContactMethods,
        note,
      },
    });

    analytics.trackForm({
      pageType: analytics.pageTypes.CHECKOUT,
      pageName: 'checkout-confirm',
      name: 'checkout',
      section: 'confirm',
      content: {
        note_provided: String(!!note),
        preferred_contact_phone: String(preferredContactMethods[PreferredContactMethod.Phone] || false),
        preferred_contact_sms: String(preferredContactMethods[PreferredContactMethod.SMS] || false),
        ...analyticsData.content,
      },
    });

    setSubmitting(true);

    // note and contactMethods are set here, so there isn't time for them to be updated in the requestData redux before the request is made
    const requestAppointmentPayload = {
      ...appointmentRequestData,
      requestData: {
        ...appointmentRequestData.requestData,
        note: note || '',
        contactMethods: contactOptionToApiPayload(preferredContactMethods),
      },
    };

    dispatch(RequestAppointment.createAction(requestAppointmentPayload));
  };

  const togglePreferredContactMethod = (method: PreferredContactMethod) => {
    if (!method) {
      return;
    }

    setPreferredContactMethods((prev) => {
      const newPreferredContactMethods = { ...prev };
      newPreferredContactMethods[method] = !newPreferredContactMethods[method];
      return newPreferredContactMethods;
    });

    analytics.trackClick({
      pageType: analytics.pageTypes.CHECKOUT,
      pageName: 'checkout-confirm',
      name: 'checkout',
      section: 'confirm',
      content: {
        action: 'preferred-contact-toggle',
        contact_method: String(method),
        ...analyticsData.content,
      },
    });
  };

  const preferredAppointmentLabel = formattedTimeSlot
    ? 'Preferred Appointment Time'
    : 'Preferred Appointment Type';

  const requestedAppointmentTypeName = requestedAppointmentType?.appointmentType?.name || requestedAppointmentType;
  const showPreferredAppointmentTime = formattedTimeSlot || requestedAppointmentTypeName;

  // Handle successful appointment request
  useEffect(() => {
    if (!newAppointment?.id || !submitting || requestAppointmentLoading || requestAppointmentError) {
      return;
    }

    setSubmitting(false);

    const matchResultsPageConfigId =
        newAppointment?.match?.matchResultsPageConfiguration?.id
        || getMatchResultsPageConfiguration()?.id;

    const { requestData, requestedSlot } = appointmentRequestData;

    analytics.trackConversion({
      pageType: analytics.pageTypes.CHECKOUT,
      pageName: clinic.name,
      name: 'form-booking',
      section: 'confirm',
      content: {
        first: String(patient?.firstName),
        last: String(patient?.lastName),
        birth: String(patient?.dateOfBirth),
        email: String(patient?.email),
        phone: String(patient?.mobilePhone),
        promo_code: String(requestData?.dmProps?.promo_code),
        date: String(requestedSlot?.date),
        time: requestedSlot && String(getTimeFromStartTime(requestedSlot.time, 'HHmm')),
        reco_appointment_booked: String(true),
        reco_step_reached: 'Booked',
        ...analyticsData.content,
      },
      userId: user?.id,
      patientId: patient?.id,
      source: requestData?.source,
      appointmentId: newAppointment?.id,
      practiceId: clinic.id,
      providerId: requestedAppointment.provider,
      partnerId: user?.partner?.id,
      isReturningPatient: newAppointment?.provider,
      heapUser: {
        age: getPatientAge(patient),
        gender: getPatientSexAtBirth(patient, newAppointment),
        funnel_config_id: newAppointment?.match?.funnelConfiguration?.id,
        match_config_id: newAppointment?.match?.configuration,
        match_results_page_config_id: matchResultsPageConfigId,
      },
      heapEvent: {
        funnel_config_id: newAppointment?.match?.funnelConfiguration?.id,
        match_id: newAppointment?.match?.id,
        match_config_id: newAppointment?.match?.configuration,
        match_results_page_config_id: matchResultsPageConfigId,
        user_id: user?.id,
        offer_value: newAppointment?.offer?.cost,
      },
    });

    cjAffiliate.trackAppointmentConversion(newAppointment);

    // Clear marketing values in funnel now that they've been attached to an appointment
    funnelStorage?.clearMarketingCache();

    // Clear the prefill data.
    funnelStorage?.clearPrefillData();

    googlePlaceActionLink.resetConversionData();

    analytics.waitForQueue().then(() => {
      setIsLoadingPatientApp(true);
      redirectionService.redirect({
        url: getPatientAppUrl(newAppointment.id),
        reason: 'appointment-confirmation',
      });
    });
  }, [
    submitting,
    requestAppointmentError,
    requestAppointmentLoading,
    appointmentRequestData,
    newAppointment,
    requestedAppointment,
    patient,
    user?.id,
    user?.partner?.id,
    clinic.id,
    clinic.name,
    clinic.address,
    clinic.primaryPhoto,
    funnelStorage,
    redirectionService,
    analyticsData,
  ]);

  const patientName = useMemo(() => {
    const nameArray = [patient.firstName, patient.lastName].filter(Boolean);
    return nameArray.join(' ');
  }, [patient.firstName, patient.lastName]);

  const formattedPhoneNumber = useMemo(() => {
    return formatPhoneNumber(patient.mobilePhone);
  }, [patient.mobilePhone]);

  return (
    <ConfirmStepWrapper>
      <StepHeader>
        <StepHeaderText>Review your Request</StepHeaderText>
        <StepHeaderSubtext>
          Please ensure the information below is
          correct before submission to your new dentist
        </StepHeaderSubtext>
      </StepHeader>

      <DetailsWrapper>
        {showPreferredAppointmentTime && (
          <DetailItem
            label={preferredAppointmentLabel}
            details={[
              { value: formattedTimeSlot, isSensitive: true },
              { value: requestedAppointmentTypeName, isSensitive: true },
            ]}
          />
        )}

        <DetailItem label="Practice">
          <PracticeDetails>
            <PracticeTextWrapper>
              <Details
                details={[
                  { value: clinic.name, isSensitive: false },
                  { value: formattedAddress, isSensitive: false },
                ]}
              />
              {isClinicUS && <InsuranceStatusBadge inNetwork={isInNetwork} applyMargin />}
            </PracticeTextWrapper>

            <PracticePhoto src={clinicPhotoUrl || ''} alt="" />
          </PracticeDetails>
        </DetailItem>

        <DetailItem
          label="Contact Details"
          details={[
            { value: patientName, isSensitive: true },
            { value: String(patient.email || ''), isSensitive: true },
            { value: formattedPhoneNumber, isSensitive: true },
          ]}
        />

        <DetailItem label="I prefer to be contacted via:" gap={12}>
          <FormRow>
            {contactOptions.map((option) => (
              <CheckboxLabeledInput key={option.contactMethod} label={option.label}>
                <FormCheckbox
                  checked={preferredContactMethods[option.contactMethod]}
                  onChange={() => togglePreferredContactMethod(option.contactMethod)}
                />
              </CheckboxLabeledInput>
            ))}
          </FormRow>

          <Input
            type={InputType.TEXTAREA}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Notes for the practice"
          />
        </DetailItem>

      </DetailsWrapper>

      <SubmitWrapper>
        <PromoCodeBox />

        <SubmitRequestWrapper>
          {requestAppointmentError && (
            <FormError>
              <ErrorMessage requestAppointmentError={requestAppointmentError} />
            </FormError>
          )}
          <SubmitRequestButton
            onClick={handleSubmit}
            $loading={requestAppointmentLoading || requestOfferLoading || isLoadingPatientApp}
          >
            Send Request
          </SubmitRequestButton>
        </SubmitRequestWrapper>
      </SubmitWrapper>

      <TermsDisclaimer>
        By requesting an appointment with Opencare, you agree to
        our <TermsOfServiceLink /> and <PrivacyLink />.
      </TermsDisclaimer>
    </ConfirmStepWrapper>
  );
};
